import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

interface Toast {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
  duration?: number;
}

interface UIState {
  isSidebarOpen: boolean;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  
  activeModal: string | null;
  modalData: any;
  openModal: (modalId: string, data?: any) => void;
  closeModal: () => void;
  
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
  
  isGlobalLoading: boolean;
  setGlobalLoading: (loading: boolean) => void;
}

export const useUIStore = create<UIState>()(
  devtools(
    persist(
      (set) => ({
        isSidebarOpen: true,
        toggleSidebar: () =>
          set((state) => ({ isSidebarOpen: !state.isSidebarOpen })),
        setSidebarOpen: (open) => set({ isSidebarOpen: open }),
        
        activeModal: null,
        modalData: null,
        openModal: (modalId, data) => 
          set({ activeModal: modalId, modalData: data }),
        closeModal: () => 
          set({ activeModal: null, modalData: null }),
        
        toasts: [],
        addToast: (toast) => {
          const id = Math.random().toString(36).substring(7);
          set((state) => ({
            toasts: [...state.toasts, { ...toast, id }],
          }));
          
          if (toast.duration !== Infinity) {
            setTimeout(() => {
              set((state) => ({
                toasts: state.toasts.filter((t) => t.id !== id),
              }));
            }, toast.duration || 5000);
          }
        },
        removeToast: (id) =>
          set((state) => ({
            toasts: state.toasts.filter((t) => t.id !== id),
          })),
        
        isGlobalLoading: false,
        setGlobalLoading: (loading) => set({ isGlobalLoading: loading }),
      }),
      {
        name: 'ui-store',
        partialize: (state) => ({ 
          isSidebarOpen: state.isSidebarOpen 
        }),
      }
    ),
    { name: 'UIStore' }
  )
);

export const toast = {
  success: (message: string, duration?: number) =>
    useUIStore.getState().addToast({ type: 'success', message, duration }),
  
  error: (message: string, duration?: number) =>
    useUIStore.getState().addToast({ type: 'error', message, duration }),
  
  warning: (message: string, duration?: number) =>
    useUIStore.getState().addToast({ type: 'warning', message, duration }),
  
  info: (message: string, duration?: number) =>
    useUIStore.getState().addToast({ type: 'info', message, duration }),
};
